import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageHeader, Btn, Input, Select, Textarea, Modal, Empty, Spinner, useToast } from '../components/shared/UI';
import RichText from '../components/shared/RichText';
import { DOC_TYPES, CURRENCIES, fmtCurrency, pricesHidden } from '../utils/helpers';
import { hasContent, toPlain } from '../utils/richText';
import { useT } from '../i18n';
import api from '../utils/api';

const emptyItem = () => ({ description: '', quantity: 1, unitPrice: 0, amount: 0 });

export default function DocumentEditor() {
  const { id } = useParams();
  const [sp]   = useSearchParams();
  const navigate  = useNavigate();
  const { show, ToastContainer } = useToast();
  const { t } = useT();
  const isEdit = Boolean(id);

  const [loading, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [docNumberMode, setDocNumberMode] = useState('auto');
  const [clients, setClients]   = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [company, setCompany]   = useState({});
  const [form, setForm] = useState({
    type:          sp.get('type') || 'quotation',
    docNumber:     '',
    title:         '',
    clientId:      '',
    issueDate:     new Date().toISOString().slice(0, 10),
    dueDate:       '',
    validUntil:    '',
    status:        'draft',
    currency:      'MYR',
    currencySymbol:'RM',
    taxRate:       0,
    discountType:  'fixed',
    discountValue: 0,
    notes:         '',
    terms:         '',
    showSignature: true,
    hidePrices:    false,
    supplierName:  '',
    supplierAddress: '',
    items:         [emptyItem()],
  });

  // Derived totals
  const subtotal = form.items.reduce((s, i) => s + (Number(i.quantity) * Number(i.unitPrice)), 0);
  const discountAmount = form.discountType === 'percent'
    ? (subtotal * Number(form.discountValue) / 100)
    : Number(form.discountValue);
  const taxable    = subtotal - discountAmount;
  const taxAmount  = taxable * Number(form.taxRate) / 100;
  const total      = taxable + taxAmount;

  useEffect(() => {
    const init = async () => {
      const [cl, co, it] = await Promise.all([api.get('/clients'), api.get('/company'), api.get('/items')]);
      setClients(cl.data);
      setSavedItems(it.data);
      const c = co.data;
      setCompany(c);
      if (!isEdit) {
        setForm(f => ({ ...f, terms: c.terms || '', currency: c.currency || 'MYR', currencySymbol: c.currencySymbol || 'RM' }));
      }
      if (isEdit) {
        const { data } = await api.get(`/documents/${id}`);
        setDocNumberMode('manual');
        setForm({
          type: data.type, status: data.status,
          docNumber: data.docNumber || '',
          title: data.title || '',
          clientId: data.client?._id || '',
          issueDate:  data.issueDate?.slice(0, 10) || '',
          dueDate:    data.dueDate?.slice(0, 10) || '',
          validUntil: data.validUntil?.slice(0, 10) || '',
          currency: data.currency, currencySymbol: data.currencySymbol,
          taxRate: data.taxRate, discountType: data.discountType,
          discountValue: data.discountValue, notes: data.notes,
          terms: data.terms, showSignature: data.showSignature !== false,
          hidePrices: data.hidePrices === true,
          supplierName: data.supplierName || '',
          supplierAddress: data.supplierAddress || '',
          items: data.items.length ? data.items : [emptyItem()],
        });
      }
    };
    init();
  }, [id, isEdit]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setCurrency = e => {
    const cur = CURRENCIES.find(c => c.code === e.target.value) || CURRENCIES[0];
    setForm(f => ({ ...f, currency: cur.code, currencySymbol: cur.symbol }));
  };

  const setItemValue = (i, k, value) => {
    setForm(f => {
      const items = [...f.items];
      items[i] = { ...items[i], [k]: value };
      items[i].amount = Number(items[i].quantity) * Number(items[i].unitPrice);
      return { ...f, items };
    });
  };
  const setItem = (i, k) => e => setItemValue(i, k, e.target.value);
  const addItem   = () => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = i => setForm(f => ({ ...f, items: f.items.filter((_, j) => j !== i) }));

  // Insert a saved item into the line items. If the last row is blank, replace it; else append.
  const insertSavedItem = (saved) => {
    setForm(f => {
      const qty = 1;
      const row = {
        description: saved.description,
        quantity: qty,
        unitPrice: saved.unitPrice,
        amount: qty * saved.unitPrice,
      };
      const items = [...f.items];
      const last = items[items.length - 1];
      const lastBlank = last && !hasContent(last.description) && !Number(last.unitPrice);
      if (lastBlank) items[items.length - 1] = row;
      else           items.push(row);
      return { ...f, items };
    });
    setPickerOpen(false);
    setPickerSearch('');
  };

  // Save an existing line as a reusable item.
  const saveLineAsItem = async (i) => {
    const item = form.items[i];
    if (!hasContent(item.description)) { show(t('editor.addDescriptionFirst'), 'error'); return; }
    try {
      const { data } = await api.post('/items', {
        // The Items library is plain text, so drop the formatting rather than
        // leaking tags into the Items page.
        description: toPlain(item.description),
        unitPrice: Number(item.unitPrice) || 0,
      });
      setSavedItems(xs => [...xs, data]);
      show(t('editor.savedToLibrary'));
    } catch { show(t('editor.couldNotSaveItem'), 'error'); }
  };

  const submit = async e => {
    e.preventDefault();
    // A contenteditable cannot carry `required`, so the check the description
    // input used to get from the browser has to be made here.
    const blank = form.items.findIndex(i => !hasContent(i.description));
    if (blank !== -1) {
      show(t('editor.itemNeedsDescription', { n: blank + 1 }), 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.dueDate)    delete payload.dueDate;
      if (!payload.validUntil) delete payload.validUntil;
      if (!payload.clientId)   delete payload.clientId;
      if (!isEdit && docNumberMode === 'auto') delete payload.docNumber;
      if (isEdit) await api.put(`/documents/${id}`, payload);
      else        await api.post('/documents', payload);
      show(isEdit ? t('editor.docUpdated') : t('editor.docCreated'));
      setTimeout(() => navigate('/documents'), 800);
    } catch (err) {
      show(err.response?.data?.message || t('editor.errorSaving'), 'error');
    } finally { setSaving(false); }
  };

  const openPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/pdf/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 15000);
    } catch { show(t('editor.pdfFailed'), 'error'); }
    finally { setPdfLoading(false); }
  };

  const downloadWord = async () => {
    try {
      const res = await api.get(`/pdf/${id}/word`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${form.docNumber || id}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (err) {
      let msg = t('editor.wordFailed');
      try { const txt = await err.response?.data?.text(); const j = JSON.parse(txt); msg = j.message || msg; } catch {}
      show(msg, 'error');
    }
  };

  const sym = form.currencySymbol || 'RM';
  const fmt = n => `${sym} ${parseFloat(n).toFixed(2)}`;
  const typeLabel = t(`docType.${form.type}`);
  const noPrices = pricesHidden(form);

  return (
    <form onSubmit={submit}>
      <ToastContainer />
      <PageHeader
        title={isEdit ? t('editor.editTitle', { type: typeLabel }) : t('editor.newTitle', { type: typeLabel })}
        actions={
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="ghost" onClick={() => navigate(-1)}>{t('common.cancel')}</Btn>
            <Btn type="submit" variant="primary" disabled={loading}>
              {loading ? t('common.saving') : isEdit ? t('editor.saveChanges') : t('editor.createDocument')}
            </Btn>
          </div>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 24, alignItems: 'start' }}>
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Document info */}
          <section style={section}>
            <h3 style={sectionH}>{t('editor.documentDetails')}</h3>
            <div style={grid2}>
              <Select label={t('editor.documentType')} value={form.type} onChange={set('type')}>
                {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.icon} {t(`docType.${d.value}`)}</option>)}
              </Select>
              <Select label={t('editor.status')} value={form.status} onChange={set('status')}>
                {['draft','sent','accepted','rejected','partial','paid','overdue','cancelled'].map(s =>
                  <option key={s} value={s}>{t(`status.${s}`)}</option>
                )}
              </Select>
              <Input label={t('editor.issueDate')} type="date" value={form.issueDate} onChange={set('issueDate')} required />
              {form.type === 'quotation'
                ? <Input label={t('editor.validUntil')} type="date" value={form.validUntil} onChange={set('validUntil')} />
                : <Input label={t('editor.dueDate')} type="date" value={form.dueDate} onChange={set('dueDate')} />
              }
            </div>
            {/* Reference number */}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>{t('editor.referenceNumber')}</div>
              {isEdit ? (
                <Input value={form.docNumber} onChange={set('docNumber')} placeholder="QT-2025-0001" />
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => setDocNumberMode('auto')}
                    style={{ padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1.5px solid', borderColor: docNumberMode === 'auto' ? 'var(--navy)' : 'var(--border)', background: docNumberMode === 'auto' ? 'var(--navy)' : 'var(--input-bg)', color: docNumberMode === 'auto' ? '#fff' : 'var(--text-muted)' }}>
                    {t('editor.auto')}
                  </button>
                  <button type="button" onClick={() => setDocNumberMode('manual')}
                    style={{ padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1.5px solid', borderColor: docNumberMode === 'manual' ? 'var(--navy)' : 'var(--border)', background: docNumberMode === 'manual' ? 'var(--navy)' : 'var(--input-bg)', color: docNumberMode === 'manual' ? '#fff' : 'var(--text-muted)' }}>
                    {t('editor.manual')}
                  </button>
                  {docNumberMode === 'manual'
                    ? <Input value={form.docNumber} onChange={set('docNumber')} placeholder="QT-2025-0001" required style={{ flex: 1, minWidth: 160 }} />
                    : <span style={{ fontSize: 12, color: 'var(--text-subtle)' }}>{t('editor.autoGenerated')}</span>
                  }
                </div>
              )}
            </div>
          </section>

          {/* Client / Supplier */}
          <section style={section}>
            <h3 style={sectionH}>{form.type === 'bill' ? t('editor.supplierBillTo') : t('editor.client')}</h3>
            {form.type === 'bill' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={grid2}>
                  <Input label={t('editor.supplierName')} value={form.supplierName} onChange={set('supplierName')} placeholder={t('editor.supplierNamePh')} />
                  <Input label={t('editor.supplierAddress')} value={form.supplierAddress} onChange={set('supplierAddress')} placeholder={t('editor.supplierAddressPh')} />
                </div>
                <Select label={t('editor.billToClient')} value={form.clientId} onChange={set('clientId')}>
                  <option value="">{t('editor.selectClientPh')}</option>
                  {clients.map(c => <option key={c._id} value={c._id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>)}
                </Select>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <Select label={t('editor.selectClient')} value={form.clientId} onChange={set('clientId')} style={{ flex: 1 }}>
                  <option value="">{t('editor.noClient')}</option>
                  {clients.map(c => <option key={c._id} value={c._id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>)}
                </Select>
                <Btn variant="ghost" size="sm" onClick={() => navigate('/clients')}>{t('editor.addClient')}</Btn>
              </div>
            )}
          </section>

          {/* Line items */}
          <section style={section}>
            <h3 style={sectionH}>{t('editor.items')}</h3>
            <div style={{ marginBottom: 14 }}>
              <Input label={t('editor.titleOptional')} value={form.title} onChange={set('title')}
                placeholder={t('editor.titlePh')} />
            </div>

            {/* Delivery orders are often issued to the receiving party without pricing */}
            {form.type === 'delivery_order' && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none', marginBottom: 14, padding: '10px 12px', background: 'var(--hover-bg)', borderRadius: 8 }}>
                <input type="checkbox" checked={form.hidePrices}
                  onChange={e => setForm(f => ({ ...f, hidePrices: e.target.checked }))}
                  style={{ width: 15, height: 15, cursor: 'pointer', marginTop: 2, flexShrink: 0 }} />
                <span>
                  {t('editor.hidePrices')}
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--text-subtle)', marginTop: 2 }}>
                    {t('editor.hidePricesHint')}
                  </span>
                </span>
              </label>
            )}

            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: noPrices ? 320 : 540, borderCollapse: 'collapse', marginBottom: 12 }}>
              <thead>
                <tr style={{ background: 'var(--bg)' }}>
                  <th style={{ ...th, width: noPrices ? '80%' : '45%' }}>{t('doc.description')}</th>
                  <th style={{ ...th, width: '12%', textAlign: 'center' }}>{t('doc.qty')}</th>
                  {!noPrices && <>
                    <th style={{ ...th, width: '20%', textAlign: 'right' }}>{t('doc.unitPrice')}</th>
                    <th style={{ ...th, width: '15%', textAlign: 'right' }}>{t('doc.amount')}</th>
                  </>}
                  <th style={{ ...th, width: '8%' }}></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '6px 8px 6px 0' }}>
                      <RichText compact value={item.description}
                        onChange={v => setItemValue(i, 'description', v)}
                        placeholder={t('editor.itemDescriptionPh')} />
                    </td>
                    <td style={{ padding: '6px 4px' }}>
                      <input type="number" min="0" step="0.01" value={item.quantity} onChange={setItem(i, 'quantity')}
                        style={{ ...itemInput, textAlign: 'center' }} />
                    </td>
                    {!noPrices && <>
                      <td style={{ padding: '6px 4px' }}>
                        <input type="number" min="0" step="0.01" value={item.unitPrice} onChange={setItem(i, 'unitPrice')}
                          style={{ ...itemInput, textAlign: 'right' }} />
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'right', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text)' }}>
                        {fmt(item.quantity * item.unitPrice)}
                      </td>
                    </>}
                    <td style={{ padding: '6px 0 6px 4px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button type="button" onClick={() => saveLineAsItem(i)}
                        title={t('editor.saveAsItem')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, lineHeight: 1, padding: '2px 4px' }}>💾</button>
                      {form.items.length > 1 && (
                        <button type="button" onClick={() => removeItem(i)}
                          title={t('editor.removeRow')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 16, lineHeight: 1, padding: '2px 4px' }}>×</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Btn variant="ghost" size="sm" type="button" onClick={addItem}>{t('editor.addItem')}</Btn>
              <Btn variant="ghost" size="sm" type="button" onClick={() => setPickerOpen(true)}>
                📦 {t('editor.fromSaved')} {savedItems.length > 0 ? `(${savedItems.length})` : ''}
              </Btn>
            </div>
          </section>

          {/* Notes & Terms */}
          <section style={section}>
            <h3 style={sectionH}>{t('editor.notesTerms')}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <RichText label={t('editor.notesLabel')} value={form.notes}
                onChange={v => setForm(f => ({ ...f, notes: v }))}
                placeholder={t('editor.notesPh')} minHeight={80} />
              <RichText label={t('editor.termsLabel')} value={form.terms}
                onChange={v => setForm(f => ({ ...f, terms: v }))}
                placeholder={t('editor.termsPh')} minHeight={110} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" checked={form.showSignature}
                  onChange={e => setForm(f => ({ ...f, showSignature: e.target.checked }))}
                  style={{ width: 15, height: 15, cursor: 'pointer' }} />
                {t('editor.includeSignature')}
              </label>
            </div>
          </section>
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, position: 'sticky', top: 24 }}>

          {/* Currency & Tax */}
          <section style={section}>
            <h3 style={sectionH}>{t('editor.currencyPricing')}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Select label={t('editor.currency')} value={form.currency} onChange={setCurrency}>
                {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} — {c.label}</option>)}
              </Select>
              <Input label={t('editor.taxRatePct')} type="number" min="0" max="100" step="0.1"
                value={form.taxRate} onChange={set('taxRate')} />
              <div style={{ display: 'flex', gap: 8 }}>
                <Select label={t('editor.discountType')} value={form.discountType} onChange={set('discountType')} style={{ flex: 1 }}>
                  <option value="fixed">{t('editor.fixed')} ({sym})</option>
                  <option value="percent">{t('editor.percent')} (%)</option>
                </Select>
                <Input label={t('editor.discountValue')} type="number" min="0" step="0.01"
                  value={form.discountValue} onChange={set('discountValue')} style={{ flex: 1 }} />
              </div>
            </div>
          </section>

          {/* Summary */}
          <section style={{ ...section, background: 'var(--hover-bg)' }}>
            <h3 style={sectionH}>{t('editor.summary')}</h3>
            {noPrices && (
              <div style={{ fontSize: 12, color: 'var(--text-subtle)', lineHeight: 1.6, marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
                {t('editor.pricesHiddenEditorNote')}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, opacity: noPrices ? 0.5 : 1 }}>
              {[
                { label: t('doc.subtotal'), val: subtotal },
                discountAmount > 0 && { label: `${t('doc.discount')}${form.discountType === 'percent' ? ` (${form.discountValue}%)` : ''}`, val: -discountAmount },
                Number(form.taxRate) > 0 && { label: `${t('doc.tax')} (${form.taxRate}%)`, val: taxAmount },
              ].filter(Boolean).map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)' }}>
                  <span>{r.label}</span>
                  <span>{fmt(r.val)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700, color: 'var(--heading)', borderTop: '2px solid var(--border)', paddingTop: 8, marginTop: 4 }}>
                <span>{t('doc.total')}</span>
                <span>{fmt(total)}</span>
              </div>
            </div>
          </section>

          {/* PDF / Word */}
          {isEdit && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Btn variant="outline" style={{ width: '100%', justifyContent: 'center' }}
                onClick={openPdf} disabled={pdfLoading} type="button">
                {pdfLoading ? t('editor.generating') : `🖨 ${t('editor.previewPdf')}`}
              </Btn>
              <Btn variant="outline" style={{ width: '100%', justifyContent: 'center' }}
                onClick={downloadWord} type="button">
                ⬇ {t('editor.downloadWord')}
              </Btn>
            </div>
          )}
        </div>
      </div>

      <Modal open={pickerOpen} onClose={() => { setPickerOpen(false); setPickerSearch(''); }}
        title={t('editor.pickSavedItem')} width={520}>
        {savedItems.length === 0 ? (
          <Empty icon="📦"
            message={t('editor.noSavedItems')}
            action={<Btn variant="primary" type="button" onClick={() => navigate('/items')}>{t('editor.manageItems')}</Btn>} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Input value={pickerSearch} onChange={e => setPickerSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
              placeholder={t('editor.searchItemsPh')} autoFocus />
            <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid var(--border-light)', borderRadius: 8 }}>
              {savedItems
                .filter(s => {
                  const q = pickerSearch.trim().toLowerCase();
                  if (!q) return true;
                  return s.description.toLowerCase().includes(q) ||
                         (s.category || '').toLowerCase().includes(q);
                })
                .map(s => (
                  <button key={s._id} type="button" onClick={() => insertSavedItem(s)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      width: '100%', padding: '10px 12px', background: 'transparent',
                      border: 'none', borderBottom: '1px solid var(--border-light)',
                      cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                      color: 'var(--text)', gap: 12,
                    }}
                    onMouseOver={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.description}
                      </div>
                      {s.category && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{s.category}</div>
                      )}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--heading)', whiteSpace: 'nowrap' }}>
                      {fmt(s.unitPrice)}{s.unit ? ` / ${s.unit}` : ''}
                    </div>
                  </button>
                ))
              }
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Btn variant="ghost" size="sm" type="button" onClick={() => navigate('/items')}>{t('editor.manageItems')}</Btn>
              <Btn variant="ghost" type="button" onClick={() => { setPickerOpen(false); setPickerSearch(''); }}>{t('common.cancel')}</Btn>
            </div>
          </div>
        )}
      </Modal>
    </form>
  );
}

const section = {
  background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 12,
  padding: '20px 24px', boxShadow: 'var(--shadow)'
};
const sectionH = { fontSize: 14, fontWeight: 600, color: 'var(--heading)', marginBottom: 14 };
const grid2    = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 12 };
const th       = { padding: '10px 8px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
                   textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border)' };
const itemInput = { width: '100%', padding: '8px', border: '1px solid var(--border)', borderRadius: 6,
                    fontSize: 13, fontFamily: 'inherit', background: 'var(--input-bg)', color: 'var(--text)', outline: 'none' };
